'use client'

// imports
import { useState } from 'react';

// lib files
import { StationData } from '@/lib/types/api/stations-data';
import { VideoClass, VideoTypeEnum } from '@/lib/types/api/video';

// components
import ITSImage from '@/components/ITSImage/ITSImage';
import MezzanineVideoInitializer from '@/components/MezzanineVideoInitializer/MezzanineVideoInitializer';
import VideoPlayer from  '@/components/VideoPlayer/VideoPlayer';

// styles
import styles from './ExploreHubHero.module.scss';

interface ExploreHubHeroProps {
  titleText: string;
  descriptionText: string;
  backgroundImage?: string;
  videoTitle?: string;
  video?: VideoClass;
  isSVP: boolean;
  stationData?: StationData;
}

const ExploreHubHero: React.FC<ExploreHubHeroProps> = (props) => {
  const {
    titleText,
    descriptionText,
    backgroundImage,
    videoTitle,
    isSVP,
    stationData,
    video,
  } = props;
  const [showVideo, setShowVideo] = useState(false);

  const ExploreHubHeroOverlay = (props: { isLoaded: boolean}) => {
    const { isLoaded } = props;
    return (
      <div className={isLoaded ? styles.explore_hub_hero_loaded_overlay : styles.explore_hub_hero_initial_overlay}>
        <div className={styles.explore_hub_hero__overlay_content}>
          <h1 className={styles.explore_hub_hero__title}>
            {titleText}
          </h1>
          <p className={styles.video_description}>
            {descriptionText}
          </p>
        </div>
      </div>
    )
  }

  const InitialExploreHubHero = () => {
    const hasMezzanine = video && !!backgroundImage;
    const hasBackgroundOnly = !video && !!backgroundImage;
    const initialClassName = (hasMezzanine || hasBackgroundOnly)
      ? styles.explore_hub_hero_initial
      : `${styles.explore_hub_hero_initial} ${styles.explore_hub_hero_initial__no_mezzanine}`;

    return (
      <div className={initialClassName}>
        <ExploreHubHeroOverlay isLoaded={false} />
        {hasMezzanine && (
          <MezzanineVideoInitializer
            videoType={video?.video_type || VideoTypeEnum.Video }
            imgSrc={backgroundImage}
            alt={videoTitle || titleText }
            onClick={() => setShowVideo(true)}
            verticalOffsetButton={true}
            width={1700}
            className={styles.explore_hub_hero_video_initializer}
            customButtonText="Video"
          />
        )}
        {hasBackgroundOnly && backgroundImage && (
          <ITSImage
            src={backgroundImage}
            alt={titleText}
            width={1700}
            className={styles.explore_hub_hero_video_initializer}
          />
        )}
      </div>
  )};

  const VideoPlayerLoaded = () => {
    const playerConfig = {
      autoplay: true,
      embedType: isSVP ? 'stationplayer' : 'portalplayer',
      disableContinuousPlay: true,
      previewLayout: true,
      stationId: stationData?.id,
      callsign: stationData?.attributes.call_sign,
    }

    return (
      <div className={styles.explore_hub_hero_loaded}>
        <ExploreHubHeroOverlay isLoaded={true} />
        <VideoPlayer
          // This non-null assertion is safe because the VideoPlayer
          // is only mounted in cases when there *is* a video asset.
          video={video!}
          playerConfig={playerConfig}
          className={styles.explore_hub_hero_loaded_video_player}
        />
      </div>
    )
  };

  return (
    showVideo && video ?
      (<section>
        <VideoPlayerLoaded />
      </section>) :
      (<section>
        <InitialExploreHubHero />
      </section>)
  );
}

export default ExploreHubHero;
